/**
 * A geração do plano, como DOIS trabalhos independentes.
 *
 * O `generateCompletePlan` é uma chamada só, mas produz duas coisas — o
 * treino e a dieta — e grava cada uma no seu próprio lugar. Do lado do app,
 * tratá-las como um bloco único significava esperar as duas para mostrar
 * qualquer uma. Aqui cada artefato tem estado próprio:
 *
 *   idle → gerando → pronto | erro
 *
 * COMO SABEMOS QUE FICOU PRONTO: não pela resposta da chamada (que pode
 * estourar o timeout do cliente com o backend ainda trabalhando), e sim pelo
 * `GENERATED_COUNT` do doc `PLANO_NUTRICIONAL/CURRENT`. Anotamos o contador
 * de cada artefato ANTES de disparar; quando o contador sobe, aquele
 * artefato ficou pronto. É o mesmo sinal que o backend usa para si, então
 * não há como divergir — e funciona mesmo se o app for fechado e reaberto no
 * meio, porque o baseline fica no `localStorage`.
 *
 * O QUE ISTO NÃO FAZ: gerar. Quem chama a Cloud Function é o `PlataformaPessoal`
 * (que já tinha a lógica de tolerância a timeout); este provider recebe a
 * promessa e observa o Firestore.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { auth, db } from '../../firebaseConfig';
import { invalidarPorPlanoGerado } from '../caminho/caminhoService';

const ARTEFATOS = ['DIETA', 'TREINO'];

/** Onde o baseline sobrevive a um fechamento do app no meio da geração. */
const CHAVE_BASE = 'lv.planoGeracao.baseline';

const Contexto = createContext(null);

/** @returns {{estado:object, gerando:boolean, iniciar:Function, ...}} */
export function usePlanoGeracao() {
  const ctx = useContext(Contexto);
  if (!ctx) {
    // Sem provider (telas soltas, testes) o consumidor não deve quebrar:
    // devolve um estado parado que não mostra nada.
    return {
      estado: { DIETA: 'idle', TREINO: 'idle' },
      resultadoAberto: null,
      avisoInicio: false,
      algumGerando: false,
      iniciar: () => {},
      abrirResultado: () => {},
      fecharResultado: () => {},
      fecharAvisoInicio: () => {},
      dispensar: () => {},
    };
  }
  return ctx;
}

const lerBaseline = () => {
  try {
    return JSON.parse(localStorage.getItem(CHAVE_BASE) || 'null');
  } catch {
    return null;
  }
};

const gravarBaseline = (valor) => {
  try {
    if (valor) localStorage.setItem(CHAVE_BASE, JSON.stringify(valor));
    else localStorage.removeItem(CHAVE_BASE);
  } catch {
    /* modo privado: seguimos sem persistir, o estado em memória basta */
  }
};

export function PlanoGeracaoProvider({ children, onGerar }) {
  const [estado, setEstado] = useState({ DIETA: 'idle', TREINO: 'idle' });
  const [resultadoAberto, setResultadoAberto] = useState(null);
  const [avisoInicio, setAvisoInicio] = useState(false);
  const baselineRef = useRef(lerBaseline());

  const marcar = useCallback((tipo, valor) => {
    setEstado((atual) => (atual[tipo] === valor ? atual : { ...atual, [tipo]: valor }));
  }, []);

  /* Retoma uma geração que ficou pelo caminho: se há baseline gravado, o app
     foi fechado no meio. Sem isto, reabrir o app durante a geração perdia os
     dois botões e a pessoa não tinha como saber que algo ainda vinha. */
  useEffect(() => {
    const base = baselineRef.current;
    if (!base) return;
    setEstado({
      DIETA: base.DIETA_PRONTA ? 'pronto' : 'gerando',
      TREINO: base.TREINO_PRONTO ? 'pronto' : 'gerando',
    });
  }, []);

  /* O observador do CURRENT. Fica ligado só enquanto algo está sendo gerado —
     é um listener a menos no app inteiro o resto do tempo. */
  const algumGerando = ARTEFATOS.some((t) => estado[t] === 'gerando');

  useEffect(() => {
    if (!algumGerando) return undefined;
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;

    const ref = doc(db, 'PACIENTE', uid, 'PLANO_NUTRICIONAL', 'CURRENT');
    const unsub = onSnapshot(ref, (snap) => {
      const base = baselineRef.current;
      if (!base) return;
      const contagem = (snap.exists() ? snap.data() : {})?.GENERATED_COUNT || {};

      ARTEFATOS.forEach((tipo) => {
        const antes = Number(base[tipo] ?? 0);
        const agora = Number(contagem[tipo] ?? 0);
        if (agora > antes) {
          marcar(tipo, 'pronto');
          /* O treino chegou: o Caminho que nasceu só de descanso precisa ser
             relido (o servidor já o replanejou ao gravar o treino). */
          if (tipo === 'TREINO') void invalidarPorPlanoGerado();
          // Persistido para o caso de o app ser fechado antes de a pessoa ver.
          baselineRef.current = { ...base, [`${tipo === 'DIETA' ? 'DIETA_PRONTA' : 'TREINO_PRONTO'}`]: true };
          gravarBaseline(baselineRef.current);
        }
      });
    });
    return () => unsub();
  }, [algumGerando, marcar]);

  /**
   * Dispara a geração e passa a observar os dois artefatos.
   * @param {object} respostas payload do generateCompletePlan
   */
  const iniciar = useCallback(
    async (respostas) => {
      const uid = auth.currentUser?.uid;
      if (!uid || !onGerar) return;

      /* Baseline ANTES de disparar. Lemos o contador atual direto do
         snapshot corrente via listener de uma vez só — `getDoc` seria uma
         leitura a mais e este doc já está no cache do app. */
      let base = { DIETA: 0, TREINO: 0 };
      try {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'PACIENTE', uid, 'PLANO_NUTRICIONAL', 'CURRENT'));
        const contagem = (snap.exists() ? snap.data() : {})?.GENERATED_COUNT || {};
        base = { DIETA: Number(contagem.DIETA ?? 0), TREINO: Number(contagem.TREINO ?? 0) };
      } catch {
        /* sem baseline lido, 0/0 é conservador: o primeiro incremento conta */
      }

      baselineRef.current = base;
      gravarBaseline(base);
      setEstado({ DIETA: 'gerando', TREINO: 'gerando' });
      setAvisoInicio(true);

      try {
        const resp = await onGerar(respostas);
        /* Cota: treino e dieta têm cotas separadas. O que ficou de fora por
           cota (resp.bloqueados, ou a recusa inteira) não é erro — o shell já
           abriu o Premium; o botão daquele artefato simplesmente não acende. */
        const bloq = resp?.cotaEsgotada
          ? { DIETA: true, TREINO: true }
          : (resp?.bloqueados || {});
        /* A chamada voltou. Quem ainda não subiu o contador não vai subir:
           a Edge daquele artefato falhou e o backend seguiu sem ele. */
        setEstado((atual) => {
          const fim = (tipo) => {
            if (atual[tipo] !== 'gerando') return atual[tipo];
            return bloq[tipo] ? 'idle' : 'erro';
          };
          return { DIETA: fim('DIETA'), TREINO: fim('TREINO') };
        });
        // Nada foi gerado (as duas cotas esgotadas): nada a observar nem avisar.
        if (bloq.DIETA && bloq.TREINO) {
          baselineRef.current = null;
          gravarBaseline(null);
          setAvisoInicio(false);
        }
      } catch {
        setEstado((atual) => ({
          DIETA: atual.DIETA === 'gerando' ? 'erro' : atual.DIETA,
          TREINO: atual.TREINO === 'gerando' ? 'erro' : atual.TREINO,
        }));
      }
    },
    [onGerar]
  );

  const abrirResultado = useCallback((tipo) => setResultadoAberto(tipo), []);
  const fecharResultado = useCallback(() => setResultadoAberto(null), []);
  const fecharAvisoInicio = useCallback(() => setAvisoInicio(false), []);

  /** Tira o botão da tela — o plano continua lá, na aba dele. */
  const dispensar = useCallback((tipo) => {
    setEstado((atual) => {
      const proximo = { ...atual, [tipo]: 'idle' };
      // Com os dois dispensados não há mais nada a observar: o baseline
      // gravado só existe para sobreviver a um fechamento do app NO MEIO da
      // geração, e mantê-lo depois faria o próximo boot acender botões de
      // uma geração que a pessoa já viu.
      if (ARTEFATOS.every((t) => proximo[t] === 'idle')) {
        baselineRef.current = null;
        gravarBaseline(null);
      }
      return proximo;
    });
  }, []);

  const valor = useMemo(
    () => ({
      estado,
      algumGerando,
      resultadoAberto,
      avisoInicio,
      iniciar,
      abrirResultado,
      fecharResultado,
      fecharAvisoInicio,
      dispensar,
    }),
    [estado, algumGerando, resultadoAberto, avisoInicio, iniciar, abrirResultado, fecharResultado, fecharAvisoInicio, dispensar]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Ponte para quem está FORA do provider e precisa disparar uma geração — o
 * caso é o próprio shell, que monta o provider e também é dono das abas
 * Dieta e Treino, onde ficam os botões de "gerar de novo".
 *
 * Sem esta ponte, uma regeneração pedida pela aba chamaria a Cloud Function
 * direto e os dois botões flutuantes nunca apareceriam — a mesma geração
 * teria duas experiências diferentes dependendo de onde foi pedida.
 */
export function PonteGeracaoDePlano({ aoPronto }) {
  const { iniciar } = usePlanoGeracao();
  useEffect(() => {
    aoPronto(iniciar);
    return () => aoPronto(null);
  }, [iniciar, aoPronto]);
  return null;
}

export default PlanoGeracaoProvider;
